// ════════════════════════════════════════════════════════════════════════════
// Risque huile : stock physique / droits clients / découvert, et conversion
// d'une dette en nature (kg d'huile) en dette monétaire (TND).
//
// RÈGLES MÉTIER
//  • Stock physique : kg réellement présents en cuve (hors cuve virtuelle de
//    sortie directe).
//  • Droit client = dépôts nets acquis − restitutions physiques − kg convertis
//    en dette monétaire. Le droit est indépendant de la cuve d'origine.
//  • Découvert (par famille substituable) = max(0 ; droits − physique mobilisable).
//  • Une vente diminue le physique et NE touche PAS aux droits (elle peut donc
//    créer ou creuser un découvert : autorisé, jamais bloquant).
//  • Une reconstitution (production huilerie / achat externe) augmente le
//    physique sans créditer une seconde fois le compte huile du client.
//  • Un nouveau dépôt client augmente physique ET droits : le découvert absolu
//    déjà constitué reste inchangé.
// ════════════════════════════════════════════════════════════════════════════
import {
  readReceptionsActives as read,
  readCuvesPubliques as readCuves,
  readRetraitsPublics as readRetraits,
  decompte,
  familleDe,
  nomTiers,
  CUVE_SORTIE_DIRECTE_ID,
  fetchFinances,
  tracer,
} from './dataProvider';
import { getCoursDuJour } from './cours';
import { envoyerCommande } from './financeCommandes';
import { ecrireLocalSeulement } from './store';

const CONV_KEY = 'oliva.conversions';
export const readConversions = () => JSON.parse(localStorage.getItem(CONV_KEY) ?? '[]');
const writeConversions = (rows) => localStorage.setItem(CONV_KEY, JSON.stringify(rows));

export const FAMILLES = [
  { id: 'noble', label: 'Extra-Vierge / Vierge' },
  { id: 'lampante', label: 'Lampante' },
];

const familleCuve = (c) => (c.famille === 'lampante' ? 'lampante' : 'noble');

// Conversions effectives : celles dont l'accord a pris effet (immédiat, ou au
// paiement lorsque l'accord le prévoit et que la dette a été réglée).
export const conversionEffective = (c) =>
  !c.annuleLe && (c.effetAuPaiement ? Boolean(c.payeeLe) : true);

// Droits clients en kg, par tiers et par famille — indépendants de la cuve.
export function fetchDroitsHuile() {
  const cuves = readCuves();
  const famParCuve = Object.fromEntries(cuves.map((c) => [c.id, familleCuve(c)]));
  const map = {};
  const cle = (tiersId, nom, famille) => {
    const k = `${tiersId || nom}|${famille}`;
    map[k] = map[k] ?? { cle: k, tiersId: tiersId || null, nom, famille, depose: 0, retire: 0, converti: 0 };
    return map[k];
  };

  JSON.parse(localStorage.getItem('oliva.reprises_huile') ?? '[]')
    .filter((r) => ['huile_client', 'droit_huile'].includes(r.type))
    .forEach((r) => { cle(r.tiersId, nomTiers(r.tiersId), r.famille || famParCuve[r.cuveId] || 'noble').depose += Number(r.kg) || 0; });

  read()
    .filter((r) => r.parcours === 'client' && r.devenir === 'cuve' && ['Trituré', 'Décompté'].includes(r.statut))
    .forEach((r) => {
      const huile = Number(r.huileExtraite ?? decompte(r).huile) || 0;
      if (huile <= 0) return;
      const famille = famParCuve[r.cuveId] ?? (familleDe(r.qualite) === 'lampante' ? 'lampante' : 'noble');
      cle(r.tiersId, nomTiers(r.tiersId), famille).depose += huile;
    });

  readRetraits().forEach((t) => {
    const famille = famParCuve[t.cuveId] ?? (/lampante/i.test(t.qualite || '') ? 'lampante' : 'noble');
    cle(t.tiersId, t.client, famille).retire += Number(t.litres) || 0;
  });

  readConversions()
    .filter(conversionEffective)
    .forEach((c) => {
      cle(c.tiersId, c.client, c.famille).converti += Number(c.kg) || 0;
    });

  return Object.values(map)
    .map((d) => ({
      ...d,
      depose: Number(d.depose.toFixed(3)),
      retire: Number(d.retire.toFixed(3)),
      converti: Number(d.converti.toFixed(3)),
      droitKg: Number((d.depose - d.retire - d.converti).toFixed(3)),
    }))
    .filter((d) => Math.abs(d.droitKg) > 0.001 || d.converti > 0)
    .sort((a, b) => b.droitKg - a.droitKg);
}

// Dettes monétaires issues des conversions, et leur reste à payer.
export function fetchDettesConversion() {
  return readConversions()
    .filter((c) => !c.annuleLe)
    .map((c) => ({
      ...c,
      montant: Number(c.montant.toFixed(3)),
      paye: Number((c.paye || 0).toFixed(3)),
      reste: Number(Math.max(0, c.montant - (c.paye || 0)).toFixed(3)),
    }));
}

// Ventes ayant créé ou creusé un découvert (traçabilité de la décision du gérant).
export function fetchVentesDecouvert() {
  return JSON.parse(localStorage.getItem('oliva.ventes') ?? '[]')
    .filter((v) => (Number(v.empruntStockClient) || 0) > 0.001)
    .map((v) => ({
      id: v.id,
      date: v.date,
      client: v.client,
      denomination: v.denomination,
      kg: Number(v.kg) || 0,
      kgDecouvert: Number(v.empruntStockClient) || 0,
      total: Number(v.total) || 0,
      paiement: v.paiement,
      // Une vente à crédit ne génère de trésorerie qu'à son encaissement.
      encaisse: v.paiement === 'Comptant' ? Number(v.total) || 0 : Number(v.paye || 0),
    }))
    .sort((a, b) => (a.date < b.date ? 1 : -1));
}

// Tableau de risque + crash-test financier.
export function calculerRisqueHuile({
  prixNoble,
  prixLampante,
  haussePct = 0,
  fraisPct = 0,
  reserve = 0,
  engagements = 0,
  creditsMobilisables = 0,
  inclureCredits = false,
  seuilSecurite = 10,
  finances,
  venteSimuleeKg = 0,
  venteSimuleeFamille = 'noble',
} = {}) {
  const cours = getCoursDuJour();
  const cuves = readCuves().filter((c) => c.id !== CUVE_SORTIE_DIRECTE_ID);
  const droits = fetchDroitsHuile();
  const prixSaisi = { noble: Number(prixNoble), lampante: Number(prixLampante) };

  const familles = FAMILLES.map(({ id, label }) => {
    const physique = Number(
      cuves.filter((c) => familleCuve(c) === id).reduce((s, c) => s + (Number(c.volume) || 0), 0).toFixed(3)
    );
    const droitsKg = Number(droits.filter((d) => d.famille === id).reduce((s, d) => s + Math.max(0, d.droitKg), 0).toFixed(3));
    const simule = venteSimuleeFamille === id ? Math.max(0, Number(venteSimuleeKg) || 0) : 0;
    const physiqueApres = Number((physique - simule).toFixed(3));
    const prixRef = Number.isFinite(prixSaisi[id]) && prixSaisi[id] > 0 ? prixSaisi[id] : Number(cours[id]) || 0;
    const prixStresse = Number((prixRef * (1 + (Number(haussePct) || 0) / 100)).toFixed(3));
    const decouvert = Number(Math.max(0, droitsKg - physique).toFixed(3));
    const decouvertApres = Number(Math.max(0, droitsKg - physiqueApres).toFixed(3));
    const frais = 1 + (Number(fraisPct) || 0) / 100;
    return {
      id,
      label,
      physique,
      physiqueApres,
      droitsKg,
      decouvert,
      decouvertApres,
      couverture: droitsKg > 0 ? Number(((physique / droitsKg) * 100).toFixed(1)) : 100,
      prixRef,
      prixStresse,
      prixIncomplet: !(prixRef > 0),
      cout: Number((decouvert * prixRef * frais).toFixed(3)),
      coutApres: Number((decouvertApres * prixRef * frais).toFixed(3)),
      coutStresse: Number((decouvert * prixStresse * frais).toFixed(3)),
    };
  });

  const comptes = (finances?.soldes ?? []).filter((c) => c.id !== 'coffre');
  const liquidites = Number(comptes.reduce((s, c) => s + (Number(c.solde) || 0), 0).toFixed(3));
  const dettesConversion = Number(fetchDettesConversion().reduce((s, d) => s + d.reste, 0).toFixed(3));
  const capacite = Number(
    (liquidites - (Number(reserve) || 0) - (Number(engagements) || 0) + (inclureCredits ? Number(creditsMobilisables) || 0 : 0)).toFixed(3)
  );

  const besoin = Number((familles.reduce((s, f) => s + f.cout, 0) + dettesConversion).toFixed(3));
  const besoinApres = Number((familles.reduce((s, f) => s + f.coutApres, 0) + dettesConversion).toFixed(3));
  const besoinStresse = Number((familles.reduce((s, f) => s + f.coutStresse, 0) + dettesConversion).toFixed(3));
  const solde = Number((capacite - besoin).toFixed(3));
  const incomplet = familles.some((f) => f.decouvert > 0 && f.prixIncomplet);

  const statut = incomplet
    ? 'incomplet'
    : solde < 0
      ? 'rouge'
      : besoin > 0 && (solde / besoin) * 100 < (Number(seuilSecurite) || 0)
        ? 'ambre'
        : 'vert';

  return {
    familles,
    comptes: comptes.map((c) => ({ id: c.id, nom: c.nom, type: c.type, solde: Number(c.solde) || 0 })),
    liquidites,
    reserve: Number(reserve) || 0,
    engagements: Number(engagements) || 0,
    creditsMobilisables: Number(creditsMobilisables) || 0,
    inclureCredits,
    capacite,
    dettesConversion,
    besoin,
    besoinApres,
    besoinStresse,
    soldeFinancement: solde,
    soldeApresVente: Number((capacite - besoinApres).toFixed(3)),
    soldeStresse: Number((capacite - besoinStresse).toFixed(3)),
    statut,
    cours: { le: cours.le, noble: cours.noble, lampante: cours.lampante },
    droits,
  };
}

export async function fetchRisqueHuile(hypotheses = {}) {
  return calculerRisqueHuile({ ...hypotheses, finances: await fetchFinances() });
}

// Conversion d'un droit en nature (kg) en dette monétaire (TND).
export async function convertirHuileEnDette({
  tiersId,
  client,
  famille,
  kg,
  prixKg,
  dateEffet,
  echeance = '',
  reference = '',
  notes = '',
  effetAuPaiement = false,
  utilisateur,
}) {
  const q = Number(kg) || 0;
  const p = Number(prixKg) || 0;
  if (!tiersId && !client) return { error: 'Sélectionnez le client concerné.' };
  if (!FAMILLES.some((f) => f.id === famille)) return { error: 'Choisissez la qualité concernée.' };
  if (q <= 0) return { error: 'Saisissez la quantité à convertir (kg).' };
  if (p <= 0) return { error: 'Saisissez le prix négocié (TND/kg).' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateEffet ?? ''))) return { error: 'Saisissez la date d’effet de l’accord.' };

  const fiche = fetchDroitsHuile().find((d) => (tiersId ? d.tiersId === tiersId : d.nom === client) && d.famille === famille);
  const disponible = Number((fiche?.droitKg ?? 0).toFixed(3));
  if (q > disponible + 0.001)
    return { error: `Conversion supérieure au droit disponible : ${disponible.toFixed(3)} kg restants.` };

  const rows = readConversions();
  const doublon = rows.find(
    (c) =>
      !c.annuleLe &&
      c.tiersId === (tiersId || null) &&
      c.famille === famille &&
      Math.abs(c.kg - q) < 0.001 &&
      Math.abs(c.prixKg - p) < 0.001 &&
      c.dateEffet === dateEffet &&
      (c.reference || '') === String(reference || '').trim()
  );
  if (doublon)
    return { error: `Cette conversion existe déjà (${doublon.id}) : aucun mouvement supplémentaire n’a été créé.` };

  const row = {
    id: `CV-${rows.reduce((m, c) => Math.max(m, Number(String(c.id).split('-')[1]) || 0), 1000) + 1}`,
    tiersId: tiersId || null,
    client: client || nomTiers(tiersId),
    famille,
    kg: Number(q.toFixed(3)),
    prixKg: Number(p.toFixed(3)),
    montant: Number((q * p).toFixed(3)),
    dateEffet,
    echeance: String(echeance || '').trim(),
    reference: String(reference || '').trim(),
    notes: String(notes || '').trim(),
    effetAuPaiement: Boolean(effetAuPaiement),
    paye: 0,
    payeeLe: null,
    annuleLe: null,
    creeLe: new Date().toISOString(),
    par: utilisateur ?? null,
  };
  writeConversions([row, ...rows]);
  tracer({
    action: 'Création',
    entite: 'Conversion huile → dette',
    refId: row.id,
    resume: `${row.client} — ${row.kg} kg ${famille} convertis à ${row.prixKg} TND/kg = ${row.montant} TND${
      row.effetAuPaiement ? ' (effet au paiement : droits conservés jusqu’au règlement)' : ''
    }`,
    utilisateur,
  });
  return { error: null, row };
}

export async function annulerConversion({ id, motif = '', utilisateur }) {
  const rows = readConversions();
  const row = rows.find((c) => c.id === id);
  if (!row) return { error: 'Conversion introuvable.' };
  if (row.annuleLe) return { error: 'Conversion déjà annulée.' };
  if ((row.paye || 0) > 0) return { error: 'Conversion partiellement réglée : annulation impossible.' };
  writeConversions(
    rows.map((c) => (c.id === id ? { ...c, annuleLe: new Date().toISOString(), motifAnnulation: String(motif || '').trim(), annulePar: utilisateur ?? null } : c))
  );
  tracer({
    action: 'Annulation',
    entite: 'Conversion huile → dette',
    refId: id,
    resume: `${row.client} — ${row.kg} kg restitués au compte huile${motif ? ` (${motif})` : ''}`,
    utilisateur,
  });
  return { error: null };
}

// Paiement (total ou partiel) d'une dette de conversion : ne retire aucun kilo, mais
// exige une VRAIE sortie de trésorerie (audit C05). Deux chemins, jamais de dette
// réduite sans caisse :
//   • création de la sortie par le serveur (compte, date, mode et référence exigés) ;
//   • rattachement d'une sortie DÉJÀ saisie, non affectée, au même tiers, sur le même
//     compte et du montant exact — aucune seconde sortie n'est créée.
// La ligne de caisse et la dette sont écrites dans la MÊME transaction serveur.
export async function reglerConversion({ id, montant, date, compteId, mode, reference, rattacher, utilisateur }) {
  const rows = readConversions();
  const row = rows.find((c) => c.id === id && !c.annuleLe);
  if (!row) return { error: 'Conversion introuvable.' };
  const m = Number(montant) || 0;
  const reste = Number((row.montant - (row.paye || 0)).toFixed(3));
  if (m <= 0) return { error: 'Saisissez le montant réglé.' };
  if (m > reste + 0.001) return { error: `Montant supérieur au reste à payer (${reste.toFixed(3)} TND).` };
  if (!rattacher && !compteId) return { error: 'Choisissez la caisse ou la banque qui paie ce règlement.' };
  const jour = /^\d{4}-\d{2}-\d{2}$/.test(String(date ?? '')) ? String(date) : new Date().toISOString().slice(0, 10);

  const res = await envoyerCommande('conversion.regler', {
    resume: `Règlement conversion ${row.id} — ${row.client} (${m.toFixed(3)} TND)`,
    conversion: {
      conversion_id: row.id,
      montant: m,
      date: jour,
      compte_id: compteId || null,
      mode: mode || null,
      reference: reference || null,
      rattacher: rattacher ? { bucket: rattacher.bucket, id: rattacher.id } : null,
    },
  });
  if (res.error) return { error: res.error, indetermine: res.indetermine };

  // Le cache local adopte EXACTEMENT ce que la base a écrit (aucune republication).
  const c = res.conversion ?? {};
  ecrireLocalSeulement(
    CONV_KEY,
    JSON.stringify(
      readConversions().map((x) =>
        x.id === row.id
          ? {
              ...x,
              paye: c.paye ?? Number(((x.paye || 0) + m).toFixed(3)),
              payeeLe: c.payeeLe ?? x.payeeLe,
              reglements: [...(x.reglements || []), c.lien].filter(Boolean),
            }
          : x
      )
    )
  );
  window.dispatchEvent(new Event('store-maj'));
  tracer({
    action: 'Règlement',
    entite: 'Conversion huile → dette',
    refId: id,
    resume: `${row.client} — ${m.toFixed(3)} TND réglés par ${
      rattacher ? `rattachement de la sortie ${rattacher.id}` : `sortie de trésorerie ${c.lien?.id ?? ''}`
    }, reste ${Number(c.reste ?? reste - m).toFixed(3)} TND`,
    utilisateur,
  });
  return {
    error: null,
    reste: c.reste ?? Number((reste - m).toFixed(3)),
    lien: c.lien ?? null,
    rattachee: Boolean(rattacher),
  };
}

// Sorties de trésorerie déjà saisies et rattachables à une dette de conversion :
// même tiers, même compte, montant EXACTEMENT égal, pas déjà affectées (CCA,
// autre conversion ou ligne issue de la Passerelle exclues).
export async function sortiesRattachables({ tiersId, compteId, montant }) {
  const m = Number(montant) || 0;
  if (!tiersId || !compteId || m <= 0) return [];
  const fin = await fetchFinances();
  const libre = (x) =>
    !x.ccaId && !x.conversionId && !String(x.id ?? '').startsWith('PRT-') && !x.propositionId;
  const exact = (x) => Math.abs((Number(x.montant) || 0) - m) < 0.0005;
  const memeTiers = (x) => String(x.tiersId ?? '') === String(tiersId);
  return [
    ...(fin.mouvements ?? [])
      .filter((x) => x.type === 'Dépense' && x.compteId === compteId && libre(x) && exact(x) && memeTiers(x))
      .map((x) => ({ bucket: 'mouvements', id: x.id, date: x.date, libelle: x.libelle, piece: x.piece, montant: x.montant })),
    ...(fin.reglements ?? [])
      .filter((x) => x.statut === 'Payé' && x.compteId === compteId && libre(x) && exact(x) && memeTiers(x))
      .map((x) => ({ bucket: 'reglements', id: x.id, date: x.date, libelle: x.beneficiaire, piece: x.piece, montant: x.montant })),
  ].sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')));
}

// Contrôle de reprise : écart entre les droits recalculés depuis les opérations
// d'origine et le cumul « dette en nature » porté par les cuves.
export function rapprochementDroits() {
  const droits = fetchDroitsHuile();
  const cuves = readCuves().filter((c) => c.id !== CUVE_SORTIE_DIRECTE_ID);
  return FAMILLES.map(({ id, label }) => {
    const recalcule = Number(droits.filter((d) => d.famille === id).reduce((s, d) => s + Math.max(0, d.droitKg), 0).toFixed(3));
    const cuvesKg = Number(
      cuves.filter((c) => familleCuve(c) === id).reduce((s, c) => s + (Number(c.volumeClients) || 0), 0).toFixed(3)
    );
    return { famille: id, label, recalcule, cuves: cuvesKg, ecart: Number((recalcule - cuvesKg).toFixed(3)) };
  });
}
